import {
  useV2CreateQuestionnaireRecommendationMutation,
  useV2UpdateQuestionnaireRecommendationMutation,
  useV2DeleteQuestionnaireRecommendationMutation,
} from "@generated";
import type { V2QuestionnaireRecommendationResource } from "@generated";
import type { RecommendationFormValues } from "../types";

export const buildRecommendationData = (
  values: RecommendationFormValues,
  id?: string
): V2QuestionnaireRecommendationResource => {
  const attributes: any = {
    "quantity-value": values.quantityValue,
    "item-id": Number(values.itemId),
    "item-type": values.itemType,
    "condition-type": values.conditionType,
  };
  if (values.conditionType !== "always") {
    attributes["conditional-value"] = values.conditionalValue;
    attributes["comparison-value"] = values.comparisonValue;
    attributes["comparison-value-type"] = values.comparisonValueType;
  }
  // Task only: full-set replace; server's refinements= setter reads SNAKE_CASE keys
  // (quantity_value/task_id). Only rows with a chosen quantity are sent.
  if (values.itemType === "Task") {
    attributes.refinements = Object.entries(values.refinements)
      .filter(([, q]) => q !== "")
      .map(([taskId, q]) => ({ quantity_value: q, task_id: Number(taskId) }));
  }
  return {
    type: "questionnaire-recommendations",
    ...(id ? { id } : {}),
    attributes,
  } as unknown as V2QuestionnaireRecommendationResource;
};

export const useRecommendationMutations = (questionnaireId: string) => {
  const [create] = useV2CreateQuestionnaireRecommendationMutation();
  const [update] = useV2UpdateQuestionnaireRecommendationMutation();
  const [destroy] = useV2DeleteQuestionnaireRecommendationMutation();
  const qid = Number(questionnaireId);

  return {
    create: (values: RecommendationFormValues) =>
      create({
        questionnaireId: qid,
        body: { data: buildRecommendationData(values) },
      }).unwrap(),
    update: (id: string, values: RecommendationFormValues) =>
      update({
        questionnaireId: qid,
        id: id,
        body: { data: buildRecommendationData(values, id) },
      }).unwrap(),
    remove: (id: string) =>
      destroy({ questionnaireId: qid, id: id }).unwrap(),
    reorder: (id: string, position: number) =>
      update({
        questionnaireId: qid,
        id: id,
        body: {
          data: {
            type: "questionnaire-recommendations",
            id: id,
            attributes: { position },
          } as unknown as V2QuestionnaireRecommendationResource,
        },
      }).unwrap(),
  };
};

export default useRecommendationMutations;
